<template>
  <v-container>
    <v-row>
      <v-col class="d-flex justify-space-between">
        <div class="d-flex align-center">
          <v-btn text icon @click="() => $router.go(-1)">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <h1>
            {{ form.id > 0 ? $t('texts.editJob') : $t('texts.addJob') }}
          </h1>
        </div>
        <v-btn
          outlined color="primary"
          class="mr-0"
          @click="showJobPreview()"
        >
          {{ $t('texts.preview') }}
          <v-icon right>
            remove_red_eye
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-alert
            v-if="!!jobroomEntry"
            :value="!!jobroomEntry"
            :type="jobroomEntry.color"
          >
            {{ $t(`texts.${jobroomEntry.key}`) }}
            <template v-if="jobroomEntry.status === 'REJECTED'">
              <br>{{ jobroomEntry.rejection_reason }}
            </template>
          </v-alert>
          <v-card-text class="pa-5">
            <form>
              <v-container class="pa-0" grid-list-md>
                <v-row>
                  <v-col :cols="12" :sm="12" :lg="5">
                    <v-container class="pa-0">
                      <v-row>
                        <v-col :cols="12">
                          <v-checkbox
                            v-model="form.status"
                            :label="$t('texts.jobActive')"
                            color="primary"
                            true-value="ACTIVE"
                            false-value="INACTIVE"
                            hide-details
                            :disabled="editingDisabled || !profileCompleted || !hasActiveSubscription"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col :cols="12">
                          <v-text-field
                            name="jobTitle"
                            :label="$t('texts.titleJob')"
                            v-model="form.title"
                            required
                            :data-vv-as="$t('texts.titleJob')"
                            data-vv-name="jobTitle"
                            v-validate="'required'"
                            :error-messages="errors.collect('jobTitle')"
                            :disabled="editingDisabled"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col>
                          <v-autocomplete
                            :label="$t('texts.jobTitleAvam')"
                            v-model="form.avam_occupation_code"
                            :items="avamOccupationClassifications"
                            item-text="autocomplete_term"
                            item-value="avam_occupation_code"
                            required
                            :data-vv-as="$t('texts.jobTitleAvam')"
                            data-vv-name="avam_occupation_code"
                            v-validate="'required'"
                            :error-messages="errors.collect('avam_occupation_code')"
                            :disabled="editingDisabled"
                          >
                            <template v-slot:item="{ item }">
                              <v-list-item-content>
                                {{ item.autocomplete_term }}
                              </v-list-item-content>
                              <v-list-item-action v-if="item.is_subject_to_registration_requirement">
                                <v-chip color="red" small text-color="white">{{ $t('texts.subjectToRegistration') }}</v-chip>
                              </v-list-item-action>
                            </template>
                            <template v-slot:append-outer>
                              <v-chip
                                v-if="isSubjectToRegistrationRequirement"
                                color="red"
                                small text-color="white"
                              >
                                {{ $t('texts.subjectToRegistration') }}
                              </v-chip>
                              <v-tooltip color="info" top>
                                <template v-slot:activator="{ on }">
                                  <v-icon
                                    v-on="on"
                                    @click="infoDialog.open = true"
                                  >
                                    help
                                  </v-icon>
                                </template>
                                <span>{{ $t('texts.help') }}</span>
                              </v-tooltip>
                            </template>
                          </v-autocomplete>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col :cols="12" :sm="4">
                          <v-select
                            :items="jobPosition"
                            v-model="form.job_position_id"
                            :label="$t('texts.jobPosition.label')"
                            :data-vv-as="$t('texts.jobPosition.label')"
                            data-vv-name="position"
                            v-validate="'required'"
                            :error-messages="errors.collect('position')"
                            required
                            :disabled="editingDisabled"
                          />
                        </v-col>
                        <v-col :cols="12" :sm="8">
                          <v-select
                            :items="jobCategory"
                            v-model="form.job_category_id"
                            :label="$t('texts.jobCategory.label')"
                            :data-vv-as="$t('texts.jobCategory.label')"
                            data-vv-name="category"
                            v-validate="'required'"
                            :error-messages="errors.collect('category')"
                            required
                            :disabled="editingDisabled"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col :cols="12" :sm="4">
                          <v-menu
                            ref="startDate"
                            :close-on-content-click="false"
                            v-model="startDatePicker"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                            max-width="290px"
                            :return-value.sync="start_date"
                            :disabled="editingDisabled"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-on="on"
                                ref="startDateField"
                                :label="$t('texts.startOfWork')"
                                :value="start_date | formatDateMonth"
                                readonly
                                required
                                :error-messages="errors.collect('startOfWork')"
                                :disabled="editingDisabled"
                              />
                              <v-text-field
                                v-show="false"
                                v-model="start_date"
                                ref="startDateInput"
                                v-validate="'required|date_format:yyyy-MM'"
                                data-vv-name="startOfWork"
                                :data-vv-as="$t('texts.startOfWork')"
                                :disabled="editingDisabled"
                              />
                            </template>
                            <v-date-picker
                              v-model="start_date"
                              @input="$refs.startDate.save(start_date); setDefaultEndDate();"
                              header-color="primary"
                              color="primary"
                              locale="de-ch"
                              type="month"
                              :min="startDatePickerMin"
                              :max="startDatePickerMax"
                              ref="startDatePicker"
                            />
                          </v-menu>
                        </v-col>
                        <v-col :cols="12" :sm="4">
                          <v-menu
                            ref="endDate"
                            :close-on-content-click="false"
                            v-model="endDatePicker"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                            max-width="290px"
                            :return-value.sync="end_date"
                            :disabled="editingDisabled"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-on="on"
                                ref="endDateField"
                                :label="$t('texts.endOfWork')"
                                :value="end_date | formatDateMonth"
                                readonly
                                required
                                :error-messages="errors.collect('endOfWork')"
                                :disabled="editingDisabled"
                              />
                              <v-text-field
                                v-show="false"
                                v-model="end_date"
                                v-validate="'required|date_format:yyyy-MM|after:startDateInput'"
                                data-vv-name="endOfWork"
                                :data-vv-as="$t('texts.endOfWork')"
                                :disabled="editingDisabled"
                              />
                            </template>
                            <v-date-picker
                              v-model="end_date"
                              @input="$refs.endDate.save(end_date)"
                              header-color="primary"
                              color="primary"
                              locale="de-ch"
                              type="month"
                              :min="endDatePickerMin"
                              :max="endDatePickerMax"
                              ref="endDatePicker"
                            />
                          </v-menu>
                          <div v-show="false">{{ validDateRange }}</div>
                        </v-col>
                        <v-col :cols="12" :sm="4">
                          <v-select
                            v-model="form.season"
                            :items="season"
                            :label="$t('texts.season')"
                            required
                            v-validate="'required'"
                            data-vv-name="season"
                            :data-vv-as="$t('texts.season')"
                            :error-messages="errors.collect('season')"
                            :disabled="editingDisabled"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col :cols="12" :sm="4">
                          <v-checkbox
                            :label="$t('texts.fullTime')"
                            v-model="form.fulltime"
                            color="primary"
                            @change="fulltime()"
                            :disabled="editingDisabled"
                          />
                        </v-col>
                        <v-col :cols="6" :sm="4" v-if="form.fulltime === false">
                          <v-text-field
                            v-model="form.workload_min"
                            :label="$t('texts.percentMin')"
                            type="number"
                            required
                            v-validate="'required|between:10,100'"
                            data-vv-name="workload_min"
                            :data-vv-as="$t('texts.percentMin')"
                            :error-messages="errors.collect('workload_min')"
                            :disabled="editingDisabled"
                          />
                        </v-col>
                        <v-col :cols="6" :sm="4" v-if="form.fulltime === false">
                          <v-text-field
                            v-model="form.workload_max"
                            :label="$t('texts.percentMax')"
                            type="number"
                            required
                            v-validate="'required|between:10,100'"
                            data-vv-name="workload_max"
                            :data-vv-as="$t('texts.percentMax')"
                            :error-messages="errors.collect('workload_max')"
                            :disabled="editingDisabled"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col :cols="12" :sm="6">
                          <google-place-autocomplete
                            ref="googleautocomplete"
                            v-model="form.place"
                            :model="form"
                            modelKey="place"
                            :label="$t('texts.place')"
                            prependIcon="search"
                            :types="['geocode']"
                            country="ch"
                            required
                            :data-vv-as="$t('texts.place')"
                            :error-messages="errors.collect('place')"
                            :autocompleteResponse="autocompleteResponse"
                            :disabled="editingDisabled"
                          />
                        </v-col>
                        <v-col :cols="12" :sm="6">
                          <v-checkbox
                            class="mt-3"
                            :label="$t('texts.lodging')"
                            v-model="form.accommodation"
                            color="primary"
                            hide-details
                            :disabled="editingDisabled"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col :cols="12">
                          <v-autocomplete
                            :items="languageItems"
                            v-model="form.language"
                            :label="$t('texts.jobLanguage')"
                            item-text="text"
                            item-value="value"
                            required
                            :error-messages="errors.collect('language')"
                            v-validate="'required'"
                            data-vv-name="language"
                            :data-vv-as="$t('texts.jobLanguage')"
                            :disabled="editingDisabled"
                          />
                        </v-col>
                      </v-row>
                      <v-row v-if="isSubjectToRegistrationRequirement" row wrap>
                        <v-col :cols="12">
                          <v-text-field
                            v-model="form.number_of_jobs"
                            :label="$t('texts.numberOfJobs')"
                            type="number"
                            required
                            v-validate="'required|between:1,100'"
                            data-vv-name="number_of_jobs"
                            :data-vv-as="$t('texts.numberOfJobs')"
                            :error-messages="errors.collect('number_of_jobs')"
                            :disabled="editingDisabled"
                          />
                        </v-col>
                        <v-col>
                          <v-checkbox
                            class="mt-3"
                            :label="$t('texts.jobNotifiedApproved')"
                            v-model="form.jobroom_external_approved"
                            color="primary"
                            hide-details
                            :disabled="editingDisabled"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-col>
                  <v-col :cols="12" :md="12" :lg="6" :offset-lg="1">
                    <v-container class="pa-0">
                      <v-row>
                        <v-col :cols="12" class="pt-4">
                          {{ $t('texts.introduction') }}
                          <quill-editor
                            v-model="form.introduction"
                            ref="myQuillEditor"
                            :options="editorOption"
                            :disabled="editingDisabled"
                          />
                          <v-alert
                            v-if="emailIntroductionError"
                            :value="true"
                            type="error"
                          >{{ $t('texts.alert.jobContainsEmailError') }}</v-alert>
                          <h2 class="pt-3">
                            <span>{{ form.title || $t('texts.label') }} </span>
                            <span>({{ formattedWorkload }}%)</span>
                          </h2>
                          {{ $t('texts.description') }}
                          <quill-editor
                            v-model="form.description"
                            ref="myQuillEditor"
                            :data-vv-as="$t('texts.description')"
                            data-vv-name="description"
                            v-validate="'required'"
                            :options="editorOption"
                            :class="errors.has('description') ? 'quill--error' : ''"
                            :disabled="editingDisabled"
                          />
                          <span
                            v-if="errors.has('description')"
                            class="help quill-error is-danger input-group__error"
                          >{{ errors.first('description')}}</span>
                          <v-alert
                            v-if="emailDescriptionError"
                            :value="true"
                            type="error"
                          >{{ $t('texts.alert.jobContainsEmailError') }}</v-alert>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-col>
                </v-row>
              </v-container>
            </form>
          </v-card-text>
          <v-divider v-if="!isInspecting" />
          <v-card-actions class="py-3 px-5" v-if="!isInspecting">
            <v-spacer/>
            <v-btn
              color="primary"
              class="white--text"
              @click="validateBeforeSubmit()"
              :disabled="fetchJobPending || storeJobPending"
              :loading="fetchJobPending || storeJobPending"
            >
              {{ $t(`texts.${ form.id ? 'saveChanges' : 'create'}`) }}
              <v-icon right class="white--text">check_circle</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <info-dialog
      :data="infoDialog"
    >
      <template slot="title">
        {{ $t('texts.avamCodeHelp.title') }}
      </template>
      <template slot="content">
        <p>
          {{ $t('texts.avamCodeHelp.description') }}
        </p>
        <p>
          {{ $t('texts.avamCodeHelp.moreInformation') }}
          <a href="https://ricrac.ch/" target="_blank">
            {{ $t('texts.avamCodeHelp.ricac') }}
          </a>
        </p>
      </template>
    </info-dialog>
    <job-preview-dialog/>
  </v-container>
</template>

<script>
/* eslint no-nested-ternary: "off" */
import Vue from 'vue';
import { createNamespacedHelpers } from 'vuex';
import moment from 'moment';
import GooglePlaceAutocomplete from '@/components/_organisms/GooglePlaceAutocomplete.vue';
import InfoDialog from '@/components/_organisms/InfoDialog.vue';
import JobPreviewDialog from './JobPreviewDialog.vue';

const { mapState: mapCompanyProfileState } = createNamespacedHelpers(
  'company/profile',
);
const {
  mapState: mapCompanyJobsState,
  mapActions: mapCompanyJobsActions,
} = createNamespacedHelpers('company/jobs');
const { mapState: mapGeneralState } = createNamespacedHelpers('general');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'CompanyJobsJob',
  components: {
    GooglePlaceAutocomplete,
    JobPreviewDialog,
    InfoDialog,
  },
  data() {
    return {
      form: {},
      start_date: '',
      end_date: '',
      startDatePicker: false,
      endDatePicker: false,
      emailCheck: {
        introduction: false,
        description: false,
      },
      infoDialog: {
        open: false,
      },
      editorOption: {
        placeholder: this.$i18n.t('texts.editorPlaceholder'),
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['clean'],
          ],
        },
        formats: [
          'background',
          'bold',
          'color',
          'font',
          'italic',
          'size',
          'strike',
          'underline',
          'blockquote',
          'header',
          'indent',
          'list',
          'align',
          'direction',
          'formula',
        ],
      },
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  created() {
    this.generateEmptyJob();
    if (this.$route.params.id || this.$route.params.job) {
      const { job } = this.$route.params;
      Object.assign(this.form, job);
      this.start_date = moment([
        job.start_year,
        job.start_month - 1,
      ]).format('YYYY-MM');
      this.end_date = moment([
        job.end_year,
        job.end_month - 1,
      ]).format('YYYY-MM');
      if (this.$route.params.type === 'copy') {
        this.form.id = null;
        this.form.created_at = null;
        this.form.number_of_applications = 0;
        this.form.jobroom_entry = null;
        this.form.jobroom_external_approved = false;
        this.form.is_editable = true;
      }
    } else {
      this.form.zip = this.company.zip;
      this.form.place = this.company.place;
      this.form.latitude = this.company.latitude;
      this.form.longitude = this.company.longitude;
      this.form.state = this.company.state;
    }
    // this.$refs.googleautocomplete.update(this.form.place);
    this.errors.clear();
  },
  computed: {
    ...mapGeneralState([
      'avamOccupationClassifications',
    ]),
    ...mapCompanyProfileState([
      'company',
      'profileCompleted',
    ]),
    ...mapCompanyJobsState([
      'job',
      'fetchJobPending',
      'storeJobPending',
      'deleteJobPending',
    ]),
    hasActiveSubscription() {
      return this.company.active_subscription;
    },
    season() {
      return [
        { value: 'summer', text: this.$i18n.t('texts.summer') },
        { value: 'winter', text: this.$i18n.t('texts.winter') },
      ];
    },
    jobPosition() {
      return [
        { value: 1, text: this.$i18n.t('texts.jobPosition.1') },
        { value: 2, text: this.$i18n.t('texts.jobPosition.2') },
        { value: 3, text: this.$i18n.t('texts.jobPosition.3') },
        { value: 4, text: this.$i18n.t('texts.jobPosition.4') },
        { value: 5, text: this.$i18n.t('texts.jobPosition.5') },
      ];
    },
    jobCategory() {
      return [
        { value: 1, text: this.$i18n.t('texts.jobCategory.1') },
        { value: 2, text: this.$i18n.t('texts.jobCategory.2') },
        { value: 3, text: this.$i18n.t('texts.jobCategory.3') },
        { value: 4, text: this.$i18n.t('texts.jobCategory.4') },
        { value: 5, text: this.$i18n.t('texts.jobCategory.5') },
        { value: 6, text: this.$i18n.t('texts.jobCategory.6') },
        { value: 7, text: this.$i18n.t('texts.jobCategory.7') },
      ];
    },
    languageItems() {
      return [
        { value: 'de', text: this.$i18n.t('texts.language.de') },
        { value: 'it', text: this.$i18n.t('texts.language.it') },
        { value: 'en', text: this.$i18n.t('texts.language.en') },
        { value: 'fr', text: this.$i18n.t('texts.language.fr') },
      ];
    },
    startDatePickerMin() {
      return moment().format('YYYY-MM');
    },
    startDatePickerMax() {
      return moment()
        .add(36, 'months')
        .format('YYYY-MM');
    },
    endDatePickerMin() {
      if (this.start_date !== '') {
        return this.start_date;
      }
      return moment().format('YYYY-MM');
    },
    endDatePickerMax() {
      const numberOfMonthsAvailable = this.start_date ? 8 : 45;
      const startMonth = this.start_date ? moment(`${this.start_date}-01`) : moment();
      return startMonth.add(numberOfMonthsAvailable, 'months').format('YYYY-MM');
    },
    validDateRange() {
      if (!this.start_date || !this.end_date) {
        return true;
      }
      const startDate = moment(`${this.start_date}-01`);
      const endDate = moment(`${this.end_date}-01`);
      const today = moment();

      // check if is not in past
      const startDateInPast = startDate.diff(today, 'months', false) < 0;
      const endDateInPast = endDate.diff(today, 'months', false) < 0;
      if (startDateInPast || endDateInPast) {
        if (startDateInPast) {
          this.errors.add({ field: 'startOfWork', msg: '' });
        }
        if (endDateInPast) {
          this.errors.add({ field: 'endOfWork', msg: '' });
        }
        return false;
      }

      // check duration
      const numberOfMonths = endDate.diff(startDate, 'months', true);
      if (numberOfMonths + 1 > 9) {
        this.errors.add({ field: 'startOfWork', msg: '' });
        this.errors.add({ field: 'endOfWork', msg: this.$i18n.t('texts.maximalDurationExceeded') });
        return false;
      }
      this.errors.remove('startOfWork');
      this.errors.remove('endOfWork');
      return true;
    },
    emailIntroductionError() {
      if (
        this.form
        && this.form.introduction
        && this.form.introduction.length > 0
      ) {
        return this.form.introduction.search('@') > -1;
      }
      return false;
    },
    emailDescriptionError() {
      if (
        this.form
        && this.form.introduction
        && this.form.introduction.length > 0
      ) {
        return this.form.description.search('@') > -1;
      }
      return false;
    },
    formattedWorkload() {
      if (this.form.workload_min === 100) {
        return '100';
      }
      if (this.form.workload_min >= this.form.workload_max) {
        return `${this.form.workload_min}`;
      }
      return `${this.form.workload_min} - ${this.form.workload_max}`;
    },
    isSubjectToRegistrationRequirement() {
      if (this.form.avam_occupation_code) {
        const avamOccupationClassification = this.avamOccupationClassifications
          .find((item) => item.avam_occupation_code === this.form.avam_occupation_code);
        return avamOccupationClassification.is_subject_to_registration_requirement;
      }
      return false;
    },
    editingDisabled() {
      return !this.form.is_editable;
    },
    jobroomEntry() {
      if (this.form.jobroom_entry) {
        let color = '';
        let key = '';
        switch (this.form.jobroom_entry.status) {
          case 'INSPECTING':
            color = 'info';
            key = 'jobRoomWaitingActivation';
            break;
          case 'REJECTED':
            color = 'error';
            key = 'jobStatus.REJECTED';
            break;
          case 'PUBLISHED_RESTRICTED':
            color = 'warning';
            key = 'jobStatus.PUBLISHED_RESTRICTED';
            break;
          case 'PUBLISHED_PUBLIC':
          case 'ARCHIVED':
            color = 'success';
            key = 'jobStatus.PUBLISHED_PUBLIC';
            break;
          case 'CANCELLED':
            color = 'info';
            key = 'jobStatus.CANCELLED';
            break;
          default:
            color = 'info';
            key = 'jobStatus.UNSYNCRONIZED';
            break;
        }
        return { ...this.form.jobroom_entry, color, key };
      }
      return undefined;
    },
    isInspecting() {
      return !!this.form.jobroom_entry && this.form.jobroom_entry.status === 'INSPECTING';
    },
  },
  methods: {
    ...mapCompanyJobsActions([
      'storeJob',
    ]),
    ...mapMessageActions([
      'showMessageValidateError',
    ]),
    generateEmptyJob() {
      this.form = {
        id: null,
        status: this.profileCompleted && this.hasActiveSubscription ? 'ACTIVE' : 'INACTIVE',
        title: '',
        fulltime: true,
        workload_min: 100,
        workload_max: 100,
        start_month: '',
        start_year: null,
        end_month: '',
        end_year: null,
        season: '',
        accommodation: false,
        zip: null,
        place: '',
        state: '',
        latitude: '',
        longitude: '',
        job_position_id: null,
        job_category_id: null,
        introduction: '',
        description: '',
        avam_occupation_code: '',
        jobroom_entry_status: '',
        jobroom_external_approved: false,
        number_of_jobs: null,
        language: 'de',
        is_editable: true,
      };
      this.start_date = '';
      this.end_date = '';
      this.$validator.reset();
    },
    autocompleteResponse(address) {
      this.form.place = address.locality || '';
      this.form.zip = address.postal_code || '';
      this.form.latitude = address.latitude || '';
      this.form.longitude = address.longitude || '';
      this.form.state = address.administrative_area_level_1 || '';
    },
    fulltime() {
      if (this.form.fulltime) {
        this.form.workload_min = 100;
        this.form.workload_max = 100;
      } else {
        this.form.workload_min = 80;
        this.form.workload_max = 100;
      }
    },
    setWorkload() {
      this.form.workload_min = this.form.fulltime
        ? 100
        : this.form.workload_min > 0
          ? this.form.workload_min
          : null;
      this.form.workload_max = this.form.fulltime
        ? 100
        : this.form.workload_max > 0
          ? this.form.workload_max
          : null;
    },
    setDefaultEndDate() {
      if (this.end_date === '') {
        const endDate = moment(this.start_date)
          .add(1, 'M')
          .format('YYYY-MM');
        this.end_date = endDate;
        this.endDatePicker = true;
      }
    },
    formatJobForm() {
      const start = this.start_date.split('-');
      const end = this.end_date.split('-');
      this.form.start_year = parseInt(start[0], 10);
      this.form.start_month = parseInt(start[1], 10);
      this.form.end_year = parseInt(end[0], 10);
      this.form.end_month = parseInt(end[1], 10);
      this.setWorkload();
    },
    showJobPreview() {
      this.$validator.validateAll().then((result) => {
        if (
          result
          && !this.emailIntroductionError
          && !this.emailDescriptionError
        ) {
          this.formatJobForm();
          this.$eventBus.$emit('show-job-preview-dialog', { job: this.form, company: this.company });
        } else {
          this.showMessageValidateError();
        }
      });
    },
    showDeleteJobDialog(id, title) {
      this.$eventBus.$emit('show-delete-job-dialog', { id, title });
    },
    validateBeforeSubmit() {
      this.$validator.validateAll().then((result) => {
        if (
          result
          && !this.emailIntroductionError
          && !this.emailDescriptionError
        ) {
          this.formatJobForm();
          const payload = {
            companyId: this.company.id,
            form: this.form,
          };
          this.storeJob(payload).then((response) => {
            if (response.status === 'ACTIVE') {
              this.$router.push({ name: 'company.jobs.list.active' });
            } else {
              this.$router.push({ name: 'company.jobs.list.draft' });
            }
          });
        } else {
          this.showMessageValidateError();
        }
      });
    },
  },
};
</script>
